// API layer — three modes:
//   USE_MOCK=true  → returns mock data (no network calls)
//   USE_MOCK=false + local endpoint → plain fetch to REACT_APP_API_ENDPOINT
//   USE_MOCK=false + AWS endpoint   → Amplify REST (Cognito-signed requests)

import { get, post, put } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import {
  MOCK_BASELINES,
  MOCK_ROLLOUTS,
  MOCK_DEVICES,
  MOCK_TELEMETRY,
  MOCK_AUDIT,
  MOCK_USERS,
  MOCK_SEGMENTS,
} from "./mockData";

const USE_MOCK = process.env.REACT_APP_USE_MOCK_AUTH === "true";
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || "";
const IS_LOCAL = API_ENDPOINT.startsWith("http://localhost") || API_ENDPOINT.startsWith("http://127.");
const API_NAME = "TetraOTAApi";

// Base URL from REACT_APP_API_BASE_URL — trailing slash is normalised away for path construction.
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

// APIs not yet deployed — always return mock data regardless of USE_MOCK flag.
const USE_MOCK_DEVICES    = true;
const USE_MOCK_ROLLOUTS   = true;
const USE_MOCK_TELEMETRY  = true;
const USE_MOCK_AUDIT      = true;
const USE_MOCK_USERS      = true;
const USE_MOCK_SEGMENTS   = true;

const FIRMWARE_BASE = API_BASE_URL;

export function getFirmwareBinUrl(id) {
  return `${FIRMWARE_BASE}/firmware/${id}?type=bin`;
}

export function getFirmwareReleaseNotesUrl(id) {
  return `${FIRMWARE_BASE}/firmware/${id}?type=release-notes`;
}

// Retrieve the Cognito ID token for Authorization header
export async function getAuthToken() {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}

async function authHeaders(extra = {}) {
  const token = await getAuthToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

// Plain fetch with JWT authorization
async function localGet(path) {
  const headers = await authHeaders();
  const res = await fetch(`${API_ENDPOINT}${path}`, { headers });
  if (!res.ok) throw new Error(`GET ${path} → ${res.status}`);
  return res.json();
}

async function localPost(path, payload) {
  const headers = await authHeaders({ "Content-Type": "application/json" });
  const res = await fetch(`${API_ENDPOINT}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`POST ${path} → ${res.status}`);
  return res.json();
}

async function localPut(path, payload) {
  const headers = await authHeaders({ "Content-Type": "application/json" });
  const res = await fetch(`${API_ENDPOINT}${path}`, {
    method: "PUT",
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`PUT ${path} → ${res.status}`);
  return res.json();
}

async function localPatch(path, payload) {
  const headers = await authHeaders({ "Content-Type": "application/json" });
  const res = await fetch(`${API_ENDPOINT}${path}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`PATCH ${path} → ${res.status}`);
  const text = await res.text();
  return text ? JSON.parse(text) : {};
}

// Amplify (AWS API Gateway + Cognito) for production
async function amplifyGet(path) {
  const { body } = await get({ apiName: API_NAME, path }).response;
  return body.json();
}

async function amplifyPost(path, payload) {
  const { body } = await post({ apiName: API_NAME, path, options: { body: payload } }).response;
  return body.json();
}

async function amplifyPut(path, payload) {
  const { body } = await put({ apiName: API_NAME, path, options: { body: payload } }).response;
  return body.json();
}

// Plain fetch against API_BASE_URL with JWT (used when not running locally)
async function baseGet(path) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE_URL}${path}`, { headers });
  if (!res.ok) throw new Error(`GET ${path} → ${res.status}`);
  return res.json();
}

async function basePost(path, payload) {
  const headers = await authHeaders({ "Content-Type": "application/json" });
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`POST ${path} → ${res.status}`);
  return res.json();
}

async function basePut(path, payload) {
  const headers = await authHeaders({ "Content-Type": "application/json" });
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "PUT",
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`PUT ${path} → ${res.status}`);
  return res.json();
}

function apiGet(path)        { return IS_LOCAL ? localGet(path)        : API_BASE_URL ? baseGet(path)  : amplifyGet(path); }
function apiPost(path, body) { return IS_LOCAL ? localPost(path, body) : API_BASE_URL ? basePost(path, body) : amplifyPost(path, body); }
function apiPut(path, body)  { return IS_LOCAL ? localPut(path, body)  : API_BASE_URL ? basePut(path, body)  : amplifyPut(path, body); }
function apiPatch(path, body) {
  if (IS_LOCAL) return localPatch(path, body);
  // Amplify does not have a native patch helper — fall back to fetch with Cognito token
  return localPatch(path, body);
}

// ── Firmware file upload (Step 1 — file bytes only) ──────────────────────────
export async function uploadFirmwareFile({ file, fileName, onProgress }) {
  const uploadUrl = `${API_BASE_URL}/upload`;
  if (!API_BASE_URL || !file) throw new Error("Upload URL or file not configured.");

  const base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const payload = {
    file_name: fileName || file.name,
    file_data: base64,
  };

  const token = await getAuthToken();
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", uploadUrl);
    xhr.setRequestHeader("Content-Type", "application/json");
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && typeof onProgress === "function") {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); } catch { resolve({}); }
      } else {
        reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    };
    xhr.onerror = () => reject(new Error("Network error during upload."));
    xhr.send(JSON.stringify(payload));
  });
}

// ── Baselines ──────────────────────────────────────────────────────────────────
function normaliseBaseline(b) {
  return {
    ...b,
    size: b.size ?? b.file_size ?? null,
    publishedAt: b.publishedAt ?? b.published_at ?? null,
    uploadedAt: b.uploadedAt ?? b.uploaded_at ?? null,
    mandatory: b.mandatory ?? (b.type === "Mandatory") ?? false,
    state: (() => {
      const s = (b.state || "").toLowerCase();
      if (s === "published") return "PUBLISHED";
      if (s === "signed") return "SIGNED";
      if (s === "deprecated") return "DEPRECATED";
      if (s === "pending") return "PENDING";
      return "DRAFT";
    })(),
  };
}

export async function getBaselines({ page = 1, limit = 10 } = {}) {
  if (USE_MOCK && !API_BASE_URL) {
    return { items: MOCK_BASELINES, total: MOCK_BASELINES.length, page: 1, limit, totalPages: 1 };
  }
  const headers = await authHeaders();
  const res = await fetch(`${FIRMWARE_BASE}/firmware/baselines?page=${page}&limit=${limit}`, { headers });
  if (!res.ok) throw new Error(`GET /firmware/baselines \u2192 ${res.status}`);
  const data = await res.json();
  const rawItems = Array.isArray(data)
    ? data
    : data.baselines ?? data.items ?? data.firmware ?? data.data ?? [];
  return {
    items: rawItems.map(normaliseBaseline),
    total: data.total ?? rawItems.length,
    page: data.page ?? page,
    limit: data.limit ?? limit,
    totalPages: data.total_pages ?? Math.ceil((data.total ?? rawItems.length) / limit),
  };
}

export async function getBaseline(id) {
  if (USE_MOCK && !API_BASE_URL) return MOCK_BASELINES.find((b) => b.id === id) || null;
  const headers = await authHeaders();
  const res = await fetch(`${FIRMWARE_BASE}/firmware/${id}`, { headers });
  if (!res.ok) throw new Error(`GET /firmware/${id} \u2192 ${res.status}`);
  return res.json();
}

export async function getFirmwareFileInfo(id) {
  const headers = await authHeaders();
  const res = await fetch(`${FIRMWARE_BASE}/firmware/${id}/getdate`, { headers });
  if (!res.ok) throw new Error(`GET /firmware/${id}/getdate \u2192 ${res.status}`);
  return res.json();
}

export async function uploadBaseline({ file, fileName, onProgress, ...meta }) {
  const uploadUrl = `${API_BASE_URL}/upload`;
  console.log("[uploadBaseline] uploadUrl:", uploadUrl);
  console.log("[uploadBaseline] file:", file);
  // If a real base URL is configured, always call it (even in mock mode)
  if (API_BASE_URL && file) {
    console.log("[uploadBaseline] Reading file as base64...");
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]); // strip data:...;base64,
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const payload = {
      file_name: fileName || file.name,
      file_data: base64,
      version_string: meta.version,
      min_device_fw_required: meta.minFw || null,
      release_notes: meta.releaseNotes || null,
      is_mandatory: meta.mandatory ?? false,
      target_segment: meta.segment || null,
    };
    console.log("[uploadBaseline] POSTing to:", uploadUrl, "file_name:", payload.file_name);
    const body = JSON.stringify(payload);
    const token = await getAuthToken();
    const result = await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", uploadUrl);
      xhr.setRequestHeader("Content-Type", "application/json");
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && typeof onProgress === "function") {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); } catch { resolve({}); }
        } else {
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      };
      xhr.onerror = () => reject(new Error("Network error during upload."));
      xhr.send(body);
    });
    console.log("[uploadBaseline] Upload complete.");
    return result;
  }
  console.warn("[uploadBaseline] SKIPPED real API — uploadUrl:", uploadUrl, "file:", file);
  // Fallback: mock mode
  if (USE_MOCK) return { id: "fw-new-" + Date.now(), ...meta, fileName, state: "DRAFT" };
  return amplifyPost("/firmware", { fileName, ...meta });
}

export async function approveBaseline(id) {
  if (USE_MOCK) return { id, state: "APPROVED" };
  return apiPut(`/firmware/${id}/approve`, {});
}

export async function patchFirmwareMetadata(id, meta) {
  if (USE_MOCK && !API_BASE_URL) return { id, ...meta };
  const headers = await authHeaders({ "Content-Type": "application/json" });
  const res = await fetch(`${FIRMWARE_BASE}/firmware/${id}/metadata`, {
    method: "PATCH",
    headers,
    body: JSON.stringify(meta),
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    console.error(`[patchFirmwareMetadata] ${res.status}`, errText);
    throw new Error(`PATCH /firmware/${id}/metadata \u2192 ${res.status}${errText ? ": " + errText : ""}`);
  }
  const text = await res.text();
  return text ? JSON.parse(text) : {};
}

export async function publishBaseline(id, segmentId) {
  if (USE_MOCK) return { id, state: "PUBLISHED", targetSegment: segmentId };
  return apiPost(`/firmware/${id}/publish`, { segmentId });
}

// ── Rollouts ───────────────────────────────────────────────────────────────────
export async function getRollouts() {
  if (USE_MOCK || USE_MOCK_ROLLOUTS) return MOCK_ROLLOUTS;
  return apiGet("/rollouts");
}

export async function getRollout(id) {
  if (USE_MOCK || USE_MOCK_ROLLOUTS) return MOCK_ROLLOUTS.find((r) => r.id === id) || null;
  return apiGet(`/rollouts/${id}`);
}

export async function pauseRollout(id) {
  if (USE_MOCK) return { id, state: "PAUSED" };
  return apiPost(`/rollouts/${id}/pause`, {});
}

export async function resumeRollout(id) {
  if (USE_MOCK) return { id, state: "CANARY" };
  return apiPost(`/rollouts/${id}/resume`, {});
}

// ── Devices ────────────────────────────────────────────────────────────────────
export async function getDevices() {
  if (USE_MOCK || USE_MOCK_DEVICES) return MOCK_DEVICES;
  return apiGet("/devices");
}

export async function getDevice(serial) {
  if (USE_MOCK || USE_MOCK_DEVICES) return MOCK_DEVICES.find((d) => d.serial === serial) || null;
  return apiGet(`/devices/${serial}`);
}

// ── Telemetry ──────────────────────────────────────────────────────────────────
export async function getTelemetry(filters = {}) {
  if (USE_MOCK || USE_MOCK_TELEMETRY) {
    let records = [...MOCK_TELEMETRY];
    if (filters.serial) records = records.filter((t) => t.deviceSerial === filters.serial);
    if (filters.rolloutId) records = records.filter((t) => t.rolloutId === filters.rolloutId);
    return records;
  }
  const params = new URLSearchParams(filters).toString();
  return apiGet(`/telemetry?${params}`);
}

// ── Audit ──────────────────────────────────────────────────────────────────────
export async function getAuditLog() {
  if (USE_MOCK || USE_MOCK_AUDIT) return MOCK_AUDIT;
  return apiGet("/audit");
}

// ── Users ──────────────────────────────────────────────────────────────────────
export async function getUsers() {
  if (USE_MOCK || USE_MOCK_USERS) return MOCK_USERS;
  return apiGet("/admin/users");
}

export async function inviteUser(payload) {
  if (USE_MOCK) return { ...payload, sub: "sub-new-" + Date.now(), status: "FORCE_CHANGE_PASSWORD" };
  return apiPost("/admin/users", payload);
}

export async function updateUserRole(sub, role) {
  if (USE_MOCK) return { sub, role };
  return apiPut(`/admin/users/${sub}/role`, { role });
}

// ── Segments ───────────────────────────────────────────────────────────────────
export async function getSegments() {
  if (USE_MOCK || USE_MOCK_SEGMENTS) return MOCK_SEGMENTS;
  return apiGet("/admin/segments");
}

export async function createSegment(payload) {
  if (USE_MOCK) return { id: "seg-new-" + Date.now(), ...payload };
  return apiPost("/admin/segments", payload);
}
